import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import SvgIconThreeDots from '@/components/atoms/SvgIconThreeDots';
import colors from '@/theme/colors';
import { extra, text } from '@/theme/colors/palettes';
import { TUserActivationToggle } from '@/types/User.types';
import {
  Center,
  HStack,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useUserDeactivation } from '@/hooks/user/users';
import queryKeys from '@/constants/queryKeys';
import CreateNotificationModal from '@/components/molecules/CreateNotificationModal';
import useSendNotificationToUsers from '@/hooks/notifications/useSendNotificationToUsers';
import usePromoteToOrganizationAdmin from '@/hooks/user/usePromoteToOrganizationAdmin';
import PeerSupportUpgradeModal from './PeerSupportUpgradeModal';
import { NO_ID } from '@/constants/stringVars';
import usePeerSupportLeadAssign from '@/hooks/peer-support/peerSupportLeadAssign';
import usePeerSupportLeadRevoke from '@/hooks/peer-support/peerSupportLeadRevoke';
import useDowngradePeerSupportToMember from '@/hooks/peer-support/downgradePeerSupportToMember';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import { useReinviteUser } from '@/hooks/organization/organization';

import React, { useCallback, useState } from 'react';

const MAX_WIDTH = '240px';

type RosterPopoverActions =
  | 'deactivate'
  | 'reinvite'
  | 'sendNotification'
  | 'promoteToOrgAdmin'
  | 'upgradeToPeerSupport'
  | 'upgradeToPeerSupportLead'
  | 'downgradeFromPeerSupport'
  | 'downgradeFromPeerSupportLead';

type Props = {
  selectedUser: TUserActivationToggle;
  isUserOnboarded?: boolean;
  disabled?: boolean;
  disabledExplanation?: string;
  refetch: () => void;
};

const RosterActionsPopover = ({
  selectedUser,
  isUserOnboarded = false,
  disabled,
  disabledExplanation,
  refetch,
}: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const showReinviteOption = !isUserOnboarded;
  const showSendNotification = isUserOnboarded;

  const isOrgAdmin = selectedUser.roles?.includes('organizationadmin') === true;
  const isPeerSupport = selectedUser.roles?.includes('peersupport') === true;
  const isPeerSupportLead = selectedUser.roles?.includes('peersupportlead') === true;
  const isMemberCare = selectedUser.roles?.includes('membercare') === true;

  const showPromoteToOrgAdmin = !isOrgAdmin && !isPeerSupportLead && !isMemberCare;

  const showPeerSupportLeadActions = isPeerSupport && !isOrgAdmin;

  const [isReinviteModalOpen, setIsReinviteModalOpen] = useState(false);
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [isPromoteToOrgAdminModalOpen, setIsPromoteToOrgAdminModalOpen] = useState(false);

  const {
    isOpen: isSendNotificationModalOpen,
    onOpen: onSendNotificationModalOpen,
    onClose: onSendNotificationModalClose,
  } = useDisclosure();

  const { mutate: deactivate, isPending: isPendingDeactivation } = useUserDeactivation();
  const { mutate: reinvite, isPending: isReinviting } = useReinviteUser();
  const { mutate: sendNotification, isPending: isSendingNotification } = useSendNotificationToUsers(() => {
    onSendNotificationModalClose();
  });
  const { mutate: promoteToOrgAdmin, isPending: isPromotingToOrgAdmin } = usePromoteToOrganizationAdmin(() => {
    setIsPromoteToOrgAdminModalOpen(false);
    refetch();
  });

  const onConfirmDeactivate = () => {
    if (selectedUser) {
      deactivate(
        { userId: selectedUser.id, hasLastLogin: selectedUser.last_login !== '' },
        {
          onSuccess: () => {
            setIsActivationModalOpen(false);
            refetch();
          },
        },
      );
    }
  };

  const onConfirmReinvite = () => {
    if (selectedUser) {
      reinvite(
        {
          userId: selectedUser.id,
          organizationId: selectedUser.app_metadata?.organizationId ?? selectedUser.organizationId ?? NO_ID,
          queryToInvalidateOnSuccess: queryKeys.users.activatedUsers,
        },
        { onSuccess: () => setIsReinviteModalOpen(false) },
      );
    }
  };

  const [isUpgradeToPeerSupportModalOpen, setIsUpgradeToPeerSupportModalOpen] = useState(false);
  const [isUpgradeToPeerSupportLeadModalOpen, setIsUpgradeToPeerSupportLeadModalOpen] = useState(false);

  const [isDowngradeFromPeerSupportModalOpen, setIsDowngradeFromPeerSupportModalOpen] = useState(false);
  const [isDowngradeFromPeerSupportLeadModalOpen, setIsDowngradeFromPeerSupportLeadModalOpen] = useState(false);

  const { mutate: downgradePeerSupportToMemberMutate, isPending: isDowngradePending } = useDowngradePeerSupportToMember(
    () => {
      refetch();
      setIsDowngradeFromPeerSupportModalOpen(false);
    },
  );

  const downgradePeerSupportToMember = useCallback(() => {
    downgradePeerSupportToMemberMutate({
      organizationId: selectedUser?.app_metadata?.organizationId ?? NO_ID,
      userId: selectedUser?.id ?? NO_ID,
    });
  }, [selectedUser]);

  const { mutate: assignPeerSupportLead, isPending: isPendingPeerSupportLeadAssign } = usePeerSupportLeadAssign(() => {
    refetch();
    setIsUpgradeToPeerSupportLeadModalOpen(false);
  });
  const { mutate: revokePeerSupportLead, isPending: isPendingPeerSupportLeadRevoke } = usePeerSupportLeadRevoke(() => {
    refetch();
    setIsDowngradeFromPeerSupportLeadModalOpen(false);
  });

  const onActionClick = (action: RosterPopoverActions) => {
    switch (action) {
      case 'deactivate':
        setIsActivationModalOpen(true);
        break;
      case 'reinvite':
        setIsReinviteModalOpen(true);
        break;
      case 'sendNotification':
        onSendNotificationModalOpen();
        break;
      case 'promoteToOrgAdmin':
        setIsPromoteToOrgAdminModalOpen(true);
        break;
      case 'upgradeToPeerSupport':
        setIsUpgradeToPeerSupportModalOpen(true);
        break;
      case 'upgradeToPeerSupportLead':
        setIsUpgradeToPeerSupportLeadModalOpen(true);
        break;
      case 'downgradeFromPeerSupport':
        setIsDowngradeFromPeerSupportModalOpen(true);
        break;
      case 'downgradeFromPeerSupportLead':
        setIsDowngradeFromPeerSupportLeadModalOpen(true);
        break;
      default:
        break;
    }
  };

  const onDeactivate = () => onActionClick('deactivate');
  const onReinvite = () => onActionClick('reinvite');
  const onSendNotification = () => onActionClick('sendNotification');
  const onPromoteToOrgAdmin = () => onActionClick('promoteToOrgAdmin');
  const onUpgradeToPeerSupport = () => onActionClick('upgradeToPeerSupport');
  const onUpgradeToPeerSupportLead = () => onActionClick('upgradeToPeerSupportLead');
  const onDowngradeFromPeerSupport = () => onActionClick('downgradeFromPeerSupport');
  const onDowngradeFromPeerSupportLead = () => onActionClick('downgradeFromPeerSupportLead');

  return (
    <VStack>
      <Popover placement={'auto'} isOpen={isOpen} onOpen={disabled ? undefined : onOpen} onClose={onClose}>
        <Tooltip label={disabled ? disabledExplanation : undefined}>
          <HStack padding={'8px'} opacity={disabled ? 0.5 : 1}>
            <PopoverAnchor>
              <HStack width={'4px'} height={'15px'} />
            </PopoverAnchor>
            <PopoverTrigger key={selectedUser.id}>
              <Center width={'35px'} marginLeft={'-10px'} cursor={'pointer'}>
                <SvgIconThreeDots color={text.mediumBlue} />
              </Center>
            </PopoverTrigger>
          </HStack>
        </Tooltip>

        <PopoverContent
          bg="white"
          border={0}
          padding={'10px'}
          borderRadius={'8px'}
          boxShadow={'0px 6px 12px 0px #0E006224'}
          // width={'136px'}
          maxWidth={MAX_WIDTH}
          alignItems={'center'}
        >
          <CustomButton
            isTransparent
            label={'Deactivate'}
            hoverBackgroundColor="extra.red"
            labelColor={colors.text.mediumBlue}
            labelHoverColor={colors.extra.white}
            iconBeforeLabelSvgPath={svgIconPaths.userDeactivate}
            onClick={() => onDeactivate()}
            height={'35px'}
            noShadow
          />
          {showReinviteOption && (
            <CustomButton
              isTransparent
              label={'Reinvite'}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.email}
              onClick={() => onReinvite()}
              height={'35px'}
              noShadow
            />
          )}
          {showSendNotification && (
            <CustomButton
              isTransparent
              label={'Send Notification'}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.bell}
              onClick={() => onSendNotification()}
              height={'35px'}
              noShadow
            />
          )}
          {showPromoteToOrgAdmin && (
            <CustomButton
              isTransparent
              label={'Promote to Org Admin'}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.infoCircle}
              onClick={() => onPromoteToOrgAdmin?.()}
              height={'35px'}
              width={MAX_WIDTH}
              noShadow
            />
          )}
          <CustomButton
            isTransparent
            label={`${isPeerSupport ? 'Downgrade from' : 'Upgrade to'} Peer Support`}
            hoverBackgroundColor="primary.500"
            labelColor={colors.text.mediumBlue}
            labelHoverColor={colors.extra.white}
            iconBeforeLabelSvgPath={svgIconPaths.peerSupport}
            iconBeforeLabelSvgColor={isPeerSupport ? extra.red : text.mediumBlue}
            onClick={() => (isPeerSupport ? onDowngradeFromPeerSupport?.() : onUpgradeToPeerSupport?.())}
            height={'35px'}
            width={MAX_WIDTH}
            noShadow
          />
          {showPeerSupportLeadActions && (
            <CustomButton
              isTransparent
              label={`${isPeerSupportLead ? 'Downgrade from' : 'Upgrade to'} PS Lead`}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.peerSupportShield}
              iconBeforeLabelSvgColor={isPeerSupportLead ? extra.red : text.mediumBlue}
              iconBeforeLabelSvgSize={25}
              onClick={() => (isPeerSupportLead ? onDowngradeFromPeerSupportLead?.() : onUpgradeToPeerSupportLead?.())}
              height={'35px'}
              width={MAX_WIDTH}
              noShadow
            />
          )}
        </PopoverContent>

        <ConfirmationModal
          isOpen={isActivationModalOpen}
          onCancel={() => {
            setIsActivationModalOpen(false);
          }}
          title={'Confirm Deactivation'}
          confirmButtonText={'Deactivate'}
          description={`This will deactivate user ${selectedUser?.firstName} ${selectedUser?.lastName}`}
          onConfirm={onConfirmDeactivate}
          confirmButtonColor={'extra.red'}
          confirmLoading={isPendingDeactivation}
        />
        <ConfirmationModal
          isOpen={isReinviteModalOpen}
          onCancel={() => {
            setIsReinviteModalOpen(false);
          }}
          title="Confirm Reinvite"
          confirmButtonText="Reinvite"
          description={`This will send reinvite email to the user ${selectedUser?.firstName} ${selectedUser?.lastName}`}
          onConfirm={onConfirmReinvite}
          confirmButtonColor="primary.500"
          confirmLoading={isReinviting}
        />
        <CreateNotificationModal
          isOpen={isSendNotificationModalOpen}
          onClose={onSendNotificationModalClose}
          isLoading={isSendingNotification}
          title={`Send Notification to ${selectedUser?.firstName} ${selectedUser?.lastName}`}
          onSubmit={(form) => {
            if (selectedUser) {
              sendNotification({ form, userIds: [selectedUser.id] });
            }
          }}
        />
        <ConfirmationModal
          isOpen={isPromoteToOrgAdminModalOpen}
          onCancel={() => {
            setIsPromoteToOrgAdminModalOpen(false);
          }}
          title="Confirm Promotion"
          confirmButtonText="Promote"
          description={`This will promote user ${selectedUser?.firstName} ${selectedUser?.lastName} to Organization Admin`}
          onConfirm={() => {
            if (selectedUser) {
              promoteToOrgAdmin(selectedUser.id);
            }
          }}
          confirmButtonColor="primary.500"
          confirmLoading={isPromotingToOrgAdmin}
        />

        {/* Peer Support modals */}
        <PeerSupportUpgradeModal
          itemToUpgradeToPeerSupportName={`${selectedUser?.firstName} ${selectedUser?.lastName}`}
          organizationId={selectedUser?.app_metadata?.organizationId ?? NO_ID}
          peerSupportModalVisible={isUpgradeToPeerSupportModalOpen}
          setPeerSupportModalVisible={setIsUpgradeToPeerSupportModalOpen}
          userToUpgradeToPeerSupportId={selectedUser?.id ?? NO_ID}
          refetch={refetch}
        />

        <ConfirmationModal
          isOpen={isDowngradeFromPeerSupportModalOpen}
          onCancel={() => {
            setIsDowngradeFromPeerSupportModalOpen(false);
          }}
          title="Confirm Downgrade from Peer Support"
          confirmButtonText="Downgrade"
          description={`This will downgrade user ${selectedUser?.firstName} ${selectedUser?.lastName} from Peer Support`}
          onConfirm={() => {
            if (selectedUser) {
              downgradePeerSupportToMember();
            }
          }}
          confirmButtonColor="primary.500"
          confirmLoading={isDowngradePending}
        />

        {/* Peer Support Lead modals  */}
        <ConfirmationModal
          isOpen={isUpgradeToPeerSupportLeadModalOpen}
          onCancel={() => {
            setIsUpgradeToPeerSupportLeadModalOpen(false);
          }}
          title="Confirm Upgrade"
          confirmButtonText="Upgrade"
          description={`This will upgrade user ${selectedUser?.firstName} ${selectedUser?.lastName} to Peer Support Lead`}
          onConfirm={() => {
            if (selectedUser) {
              assignPeerSupportLead({
                organizationId: selectedUser?.app_metadata?.organizationId ?? NO_ID,
                userId: selectedUser.id,
              });
            }
          }}
          confirmButtonColor="primary.500"
          confirmLoading={isPendingPeerSupportLeadAssign}
        />
        <ConfirmationModal
          isOpen={isDowngradeFromPeerSupportLeadModalOpen}
          onCancel={() => {
            setIsDowngradeFromPeerSupportLeadModalOpen(false);
          }}
          title="Confirm Downgrade from PS Lead"
          confirmButtonText="Downgrade"
          description={`This will downgrade user ${selectedUser?.firstName} ${selectedUser?.lastName} from Peer Support Lead`}
          onConfirm={() => {
            if (selectedUser) {
              revokePeerSupportLead({
                organizationId: selectedUser?.app_metadata?.organizationId ?? NO_ID,
                userId: selectedUser.id,
              });
            }
          }}
          confirmButtonColor="primary.500"
          confirmLoading={isPendingPeerSupportLeadRevoke}
        />
      </Popover>
    </VStack>
  );
};

export default RosterActionsPopover;
